import {
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	inject,
	input,
	output,
	viewChild,
} from '@angular/core'
import { Subject, throttleTime } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

type ResizeHandle = 'n' | 'e' | 's' | 'w' | 'nw' | 'ne' | 'se' | 'sw'

@Component({
	selector: 'lowgile-redaction-layer',
	templateUrl: './redaction-layer.component.html',
	styleUrls: ['./redaction-layer.component.scss'],
	standalone: true,
})
export class RedactionLayerComponent {
	private changeDetectorRef = inject(ChangeDetectorRef)
	private destroyRef = inject(DestroyRef)
	private redactionLayerEl =
		viewChild<ElementRef<HTMLDivElement>>('redactionLayer')
	private cdSubject = new Subject<void>()

	redactionMode = input(false)
	redactionBoxes = input<RedactionBoxInternal[]>()

	redactionsChanged = output<RedactionBoxInternal[]>()

	currentRedactionBox: RedactionBoxInternal | undefined = undefined
	private resizing:
		| {
				box: RedactionBoxInternal
				handle: ResizeHandle
		  }
		| undefined = undefined

	constructor() {
		this.cdSubject
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				throttleTime(10, undefined, { leading: true, trailing: true }),
			)
			.subscribe(() => this.changeDetectorRef.detectChanges())
	}

	onResizeHandlePointerDown(
		event: PointerEvent,
		redactionBox: RedactionBoxInternal,
		handle: ResizeHandle,
	) {
		if (!this.redactionMode()) return
		if (event.buttons !== 1) return
		event.preventDefault()
		event.stopPropagation()

		this.resizing = { box: redactionBox, handle }
		;(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(
			event.pointerId,
		)
	}

	onPointerDown(event: PointerEvent) {
		if (!this.redactionMode()) return
		if (this.currentRedactionBox) return
		if (this.resizing) return
		if (event.buttons !== 1) return

		const target = event.target as HTMLElement
		target.setPointerCapture(event.pointerId)

		const { x, y } = this.eventCoordinatesToCanvasPercentage(event)
		this.currentRedactionBox = {
			x1: x,
			y1: y,
			x2: x,
			y2: y,
			get left() {
				return Math.min(this.x1, this.x2)
			},
			get top() {
				return Math.min(this.y1, this.y2)
			},
			get width() {
				return Math.abs(this.x2 - this.x1)
			},
			get height() {
				return Math.abs(this.y2 - this.y1)
			},
		}
		this.redactionBoxes().push(this.currentRedactionBox)
		this.cdSubject.next()
	}

	onPointerMove(event: PointerEvent) {
		if (!this.redactionMode()) return
		if (this.resizing) {
			event.preventDefault()
			event.stopPropagation()
			const { x, y } = this.eventCoordinatesToCanvasPercentage(event)
			this.applyResize(
				this.resizing.box,
				this.resizing.handle,
				Math.max(Math.min(x, 100), 0),
				Math.max(Math.min(y, 100), 0),
			)
			this.cdSubject.next()
			return
		}
		if (!this.currentRedactionBox) return
		event.preventDefault()
		event.stopPropagation()
		const { x, y } = this.eventCoordinatesToCanvasPercentage(event)
		this.currentRedactionBox.x2 = Math.max(Math.min(x, 100), 0)
		this.currentRedactionBox.y2 = Math.max(Math.min(y, 100), 0)
		this.cdSubject.next()
	}

	onPointerUp(event: PointerEvent) {
		if (!this.redactionMode()) return
		const box = this.currentRedactionBox
		if (box) {
			Object.assign<RedactionBoxInternal, Partial<RedactionBoxInternal>>(box, {
				x1: Math.min(box.x1, box.x2),
				y1: Math.min(box.y1, box.y2),
				x2: Math.max(box.x1, box.x2),
				y2: Math.max(box.y1, box.y2),
			})
		}

		const shouldEmit = !!this.resizing || !!this.currentRedactionBox
		this.currentRedactionBox = undefined
		this.resizing = undefined

		const target = event.target as HTMLElement
		if (target.hasPointerCapture(event.pointerId)) {
			target.releasePointerCapture(event.pointerId)
		}
		if (shouldEmit) this.redactionsChanged.emit(this.redactionBoxes())
	}

	removeRedactionBox(event: MouseEvent, idx: number) {
		event.preventDefault()
		event.stopPropagation()
		this.redactionBoxes().splice(idx, 1)
		this.cdSubject.next()
		this.redactionsChanged.emit(this.redactionBoxes())
	}

	private eventCoordinatesToCanvasPercentage(event: MouseEvent): {
		x: number
		y: number
	} {
		const containerBounds =
			this.redactionLayerEl().nativeElement.getBoundingClientRect()
		return {
			x: ((event.clientX - containerBounds.left) / containerBounds.width) * 100,
			y: ((event.clientY - containerBounds.top) / containerBounds.height) * 100,
		}
	}

	private applyResize(
		box: RedactionBoxInternal,
		handle: ResizeHandle,
		x: number,
		y: number,
	) {
		switch (handle) {
			case 'n':
				box.y1 = y
				return
			case 's':
				box.y2 = y
				return
			case 'e':
				box.x2 = x
				return
			case 'w':
				box.x1 = x
				return
			case 'nw':
				box.x1 = x
				box.y1 = y
				return
			case 'ne':
				box.x2 = x
				box.y1 = y
				return
			case 'se':
				box.x2 = x
				box.y2 = y
				return
			case 'sw':
				box.x1 = x
				box.y2 = y
				return
		}
	}
}

export interface RedactionBoxInternal {
	x1: number
	y1: number
	x2: number
	y2: number
	get left(): number
	get top(): number
	get width(): number
	get height(): number
}
